<template>
  <div class="pdf-container">
    <div v-if="loading" class="loading">
      <div class="loading-spinner"></div>
      <span>PDF加载中，请稍候...</span>
    </div>

    <div v-if="pdfDoc" class="pdf-view">
      <!-- 工具栏增强 -->
      <div class="tool-bar">
        <div class="tool-bar-left">
          <span class="page-info">
            <span class="page-current">{{ pageNum }}</span>
            <span class="page-separator">/</span>
            <span class="page-total">{{ totalPage }}</span>
            <span class="page-label">页</span>
          </span>
          
          <div class="btn-group">
            <button @click="prevPage" :disabled="pageNum <= 1 || renderLock" class="btn btn-prev">
              <svg viewBox="0 0 24 24" width="18" height="18">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
              </svg>
            </button>
            <button @click="nextPage" :disabled="pageNum >= totalPage || renderLock" class="btn btn-next">
              <svg viewBox="0 0 24 24" width="18" height="18">
                <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"/>
              </svg>
            </button>
          </div>

          <div class="page-jump">
            <input 
              type="number" 
              v-model.number="jumpPage" 
              @keyup.enter="jumpToPage"
              min="1"
              :max="totalPage"
              class="page-input"
            />
            <button @click="jumpToPage" class="btn btn-jump">跳转</button>
          </div>
        </div>

        <div class="tool-bar-right">
          <button @click="printPdf" :disabled="renderLock" class="btn btn-print">
            <svg viewBox="0 0 24 24" width="18" height="18" style="margin-right: 4px;">
              <path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z"/>
            </svg>
            打印
          </button>
          <button @click="downloadPdf" :disabled="renderLock" class="btn btn-download">
            <svg viewBox="0 0 24 24" width="18" height="18" style="margin-right: 4px;">
              <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
            </svg>
            下载
          </button>

          <div class="divider"></div>

          <div class="scale-control">
            <button @click="scaleOut" :disabled="renderLock" class="btn btn-scale">−</button>
            <span class="scale-value">{{ (scale * 100).toFixed(0) }}%</span>
            <button @click="scaleIn" :disabled="renderLock" class="btn btn-scale">+</button>
            <button @click="resetScale" class="btn btn-reset">重置</button>
          </div>
        </div>
      </div>

      <div class="canvas-wrap">
        <canvas ref="pdfCanvas" class="pdf-canvas"></canvas>
      </div>
    </div>
  </div>
</template>

<script>
import * as pdfjsLib from 'pdfjs-dist'
import axios from 'axios'
import { casePrint } from '@/api/client'

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`

export default {
  data() {
    return {
      loading: false,
      pdfDoc: null,
      pageNum: 1,
      totalPage: 0,
      scale: 1.3,
      jumpPage: 1,
      renderLock: false,
      pdfBuffer: null,
      fileKey: '',
      loadingTask: null
    }
  },
  beforeDestroy() {
    if (this.loadingTask) this.loadingTask.cancel()
    if (this.pdfDoc) this.pdfDoc.destroy()
  },
  mounted() {
    this.getPdfStream()
  },
  methods: {
    async getPdfStream() {
      this.loading = true
      this.pdfDoc = null
      this.pdfBuffer = null
      const canvas = this.$refs.pdfCanvas
      if (canvas) {
        const ctx = canvas.getContext('2d')
        ctx.clearRect(0, 0, canvas.width, canvas.height)
      }

      const caseId = this.$route.query.medical_id
      if (!caseId) {
        this.$message.warning('缺少病历ID，无法预览')
        this.loading = false
        return
      }

      try {
        const res = await axios({
          method: 'get',
          url: `${process.env.VUE_APP_BASE_API}crm/customerCase/printMedical`,
          params: {
            case_id: caseId
          },
          responseType: 'arraybuffer'
        })
        console.log(res)
        this.fileKey = res.headers['file-key']



        let buffer = null
        if (res instanceof ArrayBuffer) {
          buffer = res
        } else if (res.data && res.data instanceof ArrayBuffer) {
          buffer = res.data
        }

        if (!buffer || buffer.byteLength === 0) {
          this.$message.warning('未获取到有效PDF文件数据')
          return
        }

        this.pdfBuffer = buffer
        await this.renderPdf(buffer)

      } catch (err) {
        console.error('PDF加载失败', err)
        this.$message.error('PDF文件加载失败，请稍后重试')
      } finally {
        this.loading = false
      }
    },

    async renderPdf(buffer) {
      try {
        this.pageNum = 1
        this.jumpPage = 1
        if (this.loadingTask) this.loadingTask.cancel()
        this.loadingTask = pdfjsLib.getDocument({ data: buffer })
        this.pdfDoc = await this.loadingTask.promise
        this.totalPage = this.pdfDoc.numPages
        await this.drawPage(this.pageNum)
      } catch (error) {
        console.error('渲染PDF失败:', error)
        this.$message.error('PDF解析失败，文件可能损坏')
      }
    },

    async drawPage(num) {
      if (this.renderLock) return
      this.renderLock = true
      try {
        const page = await this.pdfDoc.getPage(num)
        const canvas = this.$refs.pdfCanvas
        const ctx = canvas.getContext('2d')
        const viewport = page.getViewport({ scale: this.scale })
        canvas.width = viewport.width
        canvas.height = viewport.height
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        await page.render({ canvasContext: ctx, viewport }).promise
      } catch (error) {
        console.error('绘制页面失败:', error)
      } finally {
        this.renderLock = false
      }
    },

    prevPage() {
      if (this.pageNum <= 1 || this.renderLock) return
      this.pageNum--
      this.jumpPage = this.pageNum
      this.drawPage(this.pageNum)
    },
    nextPage() {
      if (this.pageNum >= this.totalPage || this.renderLock) return
      this.pageNum++
      this.jumpPage = this.pageNum
      this.drawPage(this.pageNum)
    },

    jumpToPage() {
      let num = Number(this.jumpPage)
      if (isNaN(num) || num < 1 || num > this.totalPage) {
        this.$message.warning('输入页码超出范围')
        this.jumpPage = this.pageNum
        return
      }
      this.pageNum = num
      this.drawPage(this.pageNum)
    },

    scaleIn() {
      this.scale += 0.2
      this.drawPage(this.pageNum)
    },
    scaleOut() {
      if (this.scale <= 0.4) return
      this.scale -= 0.2
      this.drawPage(this.pageNum)
    },
    resetScale() {
      this.scale = 1.3
      this.drawPage(this.pageNum)
    },

    downloadPdf() {
      if (!this.pdfBuffer) return
      const blob = new Blob([this.pdfBuffer], { type: 'application/pdf' })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `病历_${this.$route.query.medical_id}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(a.href)
    },

    async printPdf() {
      console.log('打印PDF', this.pdfBuffer)
      if (!this.pdfBuffer || this.pdfBuffer.byteLength === 0) {
        alert('暂无PDF文件，无法打印')
        return
      }

      const blob = new Blob([this.pdfBuffer], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const iframe = document.createElement('iframe')

      iframe.style.cssText = `
        width: 1px;
        height: 1px;
        opacity: 0;
        position: absolute;
        top: 0;
        left: 0;
        border: none;
      `
      iframe.src = url
      document.body.appendChild(iframe)

      iframe.onload = () => {
        setTimeout(async () => {
          try {
            await casePrint({ file_key: this.fileKey });
            iframe.contentWindow.print()

          } catch (err) {
            console.error('打印失败', err)
          }
        }, 800) 
      }

      const clearResource = () => {
        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe)
          }
          URL.revokeObjectURL(url)
        }, 2000)
      }

      window.addEventListener('afterprint', clearResource, { once: true })
    }
  }
}
</script>

<style scoped>
.pdf-container {
  padding: 24px;
  background: #f5f7fa;
  min-height: 100vh;
}

/* Loading 样式 */
.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 30px;
  color: #5a6879;
  font-size: 15px;
  gap: 16px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e8ecf1;
  border-top-color: #33bea7;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.pdf-view {
  max-width: 1200px;
  margin: 0 auto;
}

/* 工具栏样式 */
.tool-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 20px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  margin-bottom: 20px;
}

.tool-bar-left,
.tool-bar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* 页面信息 */
.page-info {
  display: flex;
  align-items: baseline;
  gap: 2px;
  font-size: 14px;
  color: #4a5a6e;
  padding-right: 12px;
  border-right: 1px solid #e8ecf1;
}

.page-current {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
}

.page-separator {
  margin: 0 2px;
  color: #b0bec5;
}

.page-total {
  font-size: 15px;
  color: #78909c;
}

.page-label {
  margin-left: 2px;
  color: #90a4ae;
  font-size: 13px;
}

/* 按钮组 */
.btn-group {
  display: flex;
  gap: 2px;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 14px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: transparent;
  color: #4a5a6e;
  white-space: nowrap;
  height: 34px;
}


.btn:active:not(:disabled) {
  transform: scale(0.96);
}

.btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.btn-prev,
.btn-next {
  padding: 6px 8px;
  border-radius: 6px;
}

.btn-jump {
  background: #33bea7;
  color: #fff;
  padding: 6px 16px;
  border-radius: 6px;
  font-size: 13px;
}

.btn-jump:active:not(:disabled) {
  transform: scale(0.96);
}

.btn-scale {
  padding: 6px 10px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 6px;
  min-width: 32px;
}

.btn-reset {
  padding: 6px 12px;
  font-size: 12px;
  color: #78909c;
  border-radius: 6px;
}


.btn-print {
  background: #f5f7fa;
  padding: 6px 16px;
  border-radius: 6px;
}

.btn-download {
  background: #33bea7;
  color: #fff;
  padding: 6px 16px;
  border-radius: 6px;
}


.btn-download:active:not(:disabled) {
  transform: scale(0.96);
}

/* 分割线 */
.divider {
  width: 1px;
  height: 28px;
  background: #e8ecf1;
}

/* 页码跳转 */
.page-jump {
  display: flex;
  align-items: center;
  gap: 6px;
}

.page-input {
  width: 52px;
  padding: 4px 8px;
  border: 1px solid #dce0e6;
  border-radius: 6px;
  font-size: 14px;
  text-align: center;
  outline: none;
  transition: border-color 0.2s;
  height: 34px;
}

.page-input:focus {
  border-color: #33bea7;
  box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.1);
}

.page-input::-webkit-inner-spin-button,
.page-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.page-input[type="number"] {
  -moz-appearance: textfield;
}

/* 缩放控制 */
.scale-control {
  display: flex;
  align-items: center;
  gap: 4px;
}

.scale-value {
  font-size: 14px;
  font-weight: 500;
  color: #2c3e50;
  min-width: 48px;
  text-align: center;
}

/* Canvas 容器 */
.canvas-wrap {
  display: flex;
  justify-content: center;
  padding: 20px;
}

.pdf-canvas {
  border-radius: 4px;
  max-width: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  background: #ffffff;
}

/* 响应式 */
@media (max-width: 1024px) {
  .tool-bar {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    padding: 12px 16px;
  }

  .tool-bar-left,
  .tool-bar-right {
    flex-wrap: wrap;
    justify-content: center;
  }

  .page-info {
    border-right: none;
    padding-right: 0;
  }

  .divider {
    display: none;
  }
}

@media (max-width: 768px) {
  .pdf-container {
    padding: 12px;
  }

  .btn {
    font-size: 12px;
    padding: 4px 10px;
    height: 30px;
  }

  .page-input {
    width: 44px;
    height: 30px;
    font-size: 13px;
  }

  .canvas-wrap {
    padding: 12px;
  }
}

@media (max-width: 480px) {
  .tool-bar-left,
  .tool-bar-right {
    gap: 6px;
  }

  .btn {
    font-size: 11px;
    padding: 4px 8px;
    height: 28px;
  }

  .btn-prev,
  .btn-next {
    padding: 4px 6px;
  }

  .scale-value {
    min-width: 36px;
    font-size: 12px;
  }
}
</style>